import { CIRRA_SETTING_DEFINITION_BY_KEY, DEFAULT_CIRRA_SANS_FONT_SIZE_PX, DEFAULT_GENERAL_OPEN_DESTINATION_OPTIONS, GENERAL_OPEN_DESTINATION_VALUES, GENERAL_THREAD_DETAIL_OPTIONS, GENERAL_THREAD_DETAIL_VALUES, PERSONALIZATION_PERSONALITY_VALUES, PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_OPTIONS, PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_VALUES, THEME_VALUES, cirraSettingsDefinitions } from '../schema/cirraSettings.js';
import { isObject } from './isObject.js';

const CIRRA_PREF_KEY_PREFIX = 'cirra.settings.';
export const CIRRA_AUTO_THEME_DAY_START_HOUR = 5;
export const CIRRA_AUTO_THEME_NIGHT_START_HOUR = 17;
const PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_VALUE_SET = new Set(PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_VALUES);
const PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_OPTION_BY_VALUE = Object.freeze(
  Object.fromEntries(
    PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_OPTIONS.map((option) => {
      return [option.value, option];
    })
  )
);

const normalizeEnumValue = (value) => {
  return String(value || '')
    .trim()
    .toLowerCase();
};

const normalizeFontFamilyValue = (value) => {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeTextValue = (value) => {
  return String(value || '').replace(/\r\n/g, '\n');
};

const getSafeDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  return new Date();
};

const normalizeBranchPrefixValue = (value) => {
  const normalized = String(value || '').trim();

  if (!normalized) {
    return normalized;
  }

  return normalized.endsWith('/') ? normalized : `${normalized}/`;
};

const getSettingDefinition = (settingKey) => {
  const normalizedKey = String(settingKey || '').trim();

  if (!normalizedKey) {
    return null;
  }

  return CIRRA_SETTING_DEFINITION_BY_KEY[normalizedKey] || null;
};

const parseStrictSettingValue = ({ definition, value }) => {
  if (!definition) {
    return {
      success: false,
      error: {
        code: 'UNKNOWN_CIRRA_SETTING',
        message: 'Unknown Cirra setting.'
      }
    };
  }

  switch (definition.type) {
    case 'boolean': {
      if (typeof value !== 'boolean') {
        return {
          success: false,
          error: {
            code: 'INVALID_CIRRA_SETTING_VALUE',
            message: `Setting "${definition.key}" must be a boolean.`
          }
        };
      }

      return {
        success: true,
        data: value
      };
    }

    case 'enum': {
      const normalized = normalizeEnumValue(value);

      if (!normalized || !definition.enumValues.includes(normalized)) {
        return {
          success: false,
          error: {
            code: 'INVALID_CIRRA_SETTING_VALUE',
            message: `Setting "${definition.key}" must be one of: ${definition.enumValues.join(', ')}.`
          }
        };
      }

      return {
        success: true,
        data: normalized
      };
    }

    case 'integer': {
      if (!Number.isFinite(value)) {
        return {
          success: false,
          error: {
            code: 'INVALID_CIRRA_SETTING_VALUE',
            message: `Setting "${definition.key}" must be a number.`
          }
        };
      }

      const normalized = Math.round(Number(value));

      if (normalized < definition.min || normalized > definition.max) {
        return {
          success: false,
          error: {
            code: 'INVALID_CIRRA_SETTING_VALUE',
            message: `Setting "${definition.key}" must be between ${definition.min} and ${definition.max}.`
          }
        };
      }

      return {
        success: true,
        data: normalized
      };
    }

    case 'fontFamily': {
      if (typeof value !== 'string') {
        return {
          success: false,
          error: {
            code: 'INVALID_CIRRA_SETTING_VALUE',
            message: `Setting "${definition.key}" must be a string.`
          }
        };
      }

      const normalized = normalizeFontFamilyValue(value);

      if (!normalized) {
        return {
          success: false,
          error: {
            code: 'INVALID_CIRRA_SETTING_VALUE',
            message: `Setting "${definition.key}" cannot be empty.`
          }
        };
      }

      if (normalized.length > definition.maxLength) {
        return {
          success: false,
          error: {
            code: 'INVALID_CIRRA_SETTING_VALUE',
            message: `Setting "${definition.key}" must be at most ${definition.maxLength} characters.`
          }
        };
      }

      return {
        success: true,
        data: normalized
      };
    }

    case 'text': {
      if (typeof value !== 'string') {
        return {
          success: false,
          error: {
            code: 'INVALID_CIRRA_SETTING_VALUE',
            message: `Setting "${definition.key}" must be a string.`
          }
        };
      }

      const normalized = normalizeTextValue(value).trim();

      if (normalized.length > definition.maxLength) {
        return {
          success: false,
          error: {
            code: 'INVALID_CIRRA_SETTING_VALUE',
            message: `Setting "${definition.key}" must be at most ${definition.maxLength} characters.`
          }
        };
      }

      return {
        success: true,
        data: normalized
      };
    }

    case 'branchPrefix': {
      if (typeof value !== 'string') {
        return {
          success: false,
          error: {
            code: 'INVALID_CIRRA_SETTING_VALUE',
            message: `Setting "${definition.key}" must be a string.`
          }
        };
      }

      const normalized = normalizeBranchPrefixValue(value);

      if (!normalized) {
        return {
          success: false,
          error: {
            code: 'INVALID_CIRRA_SETTING_VALUE',
            message: `Setting "${definition.key}" cannot be empty.`
          }
        };
      }

      if (normalized.length > definition.maxLength) {
        return {
          success: false,
          error: {
            code: 'INVALID_CIRRA_SETTING_VALUE',
            message: `Setting "${definition.key}" must be at most ${definition.maxLength} characters.`
          }
        };
      }

      return {
        success: true,
        data: normalized
      };
    }

    default: {
      return {
        success: false,
        error: {
          code: 'INVALID_CIRRA_SETTING_DEFINITION',
          message: `Setting "${definition.key}" has an unsupported definition type.`
        }
      };
    }
  }
};

const normalizeSettingValueLoose = ({ definition, value, fallback }) => {
  if (definition?.type === 'integer') {
    const parsed = Number(value);

    if (Number.isFinite(parsed)) {
      return Math.max(definition.min, Math.min(definition.max, Math.round(parsed)));
    }
  }

  const parsed = parseStrictSettingValue({
    definition,
    value
  });

  if (parsed.success) {
    return parsed.data;
  }

  return fallback;
};

const getSourceValue = ({ source, key }) => {
  if (!isObject(source) || !key) {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(source, key)) {
    return source[key];
  }

  const definition = getSettingDefinition(key);
  const legacyGroup = definition?.legacyGroupKey;

  if (!legacyGroup) {
    return undefined;
  }

  const legacyGroupObject = isObject(source[legacyGroup]) ? source[legacyGroup] : null;

  if (!legacyGroupObject || !Object.prototype.hasOwnProperty.call(legacyGroupObject, key)) {
    return undefined;
  }

  return legacyGroupObject[key];
};

export const CIRRA_SETTING_KEYS = Object.freeze(
  cirraSettingsDefinitions.map((definition) => {
    return definition.key;
  })
);

export const CIRRA_SYNCED_SETTING_KEYS = Object.freeze(
  cirraSettingsDefinitions
    .filter((definition) => {
      return definition.persistence === 'synced';
    })
    .map((definition) => {
      return definition.key;
    })
);

export const CIRRA_LOCAL_ONLY_SETTING_KEYS = Object.freeze(
  cirraSettingsDefinitions
    .filter((definition) => {
      return definition.persistence === 'local';
    })
    .map((definition) => {
      return definition.key;
    })
);

export const CIRRA_LEGACY_GROUP_BY_SETTING_KEY = Object.freeze(
  Object.fromEntries(
    cirraSettingsDefinitions.map((definition) => {
      return [definition.key, definition.legacyGroupKey];
    })
  )
);

export const CIRRA_LEGACY_SETTING_PATH_BY_KEY = Object.freeze(
  Object.fromEntries(
    cirraSettingsDefinitions.map((definition) => {
      return [definition.key, `${definition.legacyGroupKey}.${definition.key}`];
    })
  )
);

export const getCirraSettingDefinition = (settingKey) => {
  return getSettingDefinition(settingKey);
};

export const getCirraPreferredResponseLanguageOption = (value, fallback = 'auto') => {
  const normalizedFallback = PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_VALUE_SET.has(normalizeEnumValue(fallback)) ? normalizeEnumValue(fallback) : 'auto';
  const normalizedValue = normalizeEnumValue(value);
  const resolvedValue = PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_VALUE_SET.has(normalizedValue) ? normalizedValue : normalizedFallback;

  return PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_OPTION_BY_VALUE[resolvedValue] || PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_OPTION_BY_VALUE.auto;
};

export const getCirraPreferredResponseLanguageLabel = (value, fallback = 'auto') => {
  return getCirraPreferredResponseLanguageOption(value, fallback).label;
};

export const isCirraSettingKey = (settingKey) => {
  return Boolean(getSettingDefinition(settingKey));
};

export const isCirraSyncedSettingKey = (settingKey) => {
  return getSettingDefinition(settingKey)?.persistence === 'synced';
};

export const getDefaultCirraSettings = () => {
  return Object.freeze(
    Object.fromEntries(
      cirraSettingsDefinitions.map((definition) => {
        return [definition.key, definition.defaultValue];
      })
    )
  );
};

export const getCirraSettingsPatchFromSource = (source) => {
  if (!isObject(source)) {
    return {};
  }

  const patch = {};

  for (const settingKey of CIRRA_SETTING_KEYS) {
    const settingValue = getSourceValue({
      source,
      key: settingKey
    });

    if (settingValue === undefined) {
      continue;
    }

    patch[settingKey] = settingValue;
  }

  return patch;
};

export const validateCirraSettingsPatch = (settingsPatch, options = {}) => {
  const allowedPersistences = Array.isArray(options?.persistences) && options.persistences.length > 0 ? new Set(options.persistences) : null;
  const rejectUnknownKeys = options?.rejectUnknownKeys !== false;

  if (!isObject(settingsPatch)) {
    return {
      success: false,
      error: {
        code: 'INVALID_CIRRA_SETTINGS_PATCH',
        message: 'Cirra settings patch must be an object.'
      }
    };
  }

  const normalizedPatch = {};

  for (const [rawKey, rawValue] of Object.entries(settingsPatch)) {
    const definition = getSettingDefinition(rawKey);

    if (!definition) {
      if (rejectUnknownKeys) {
        return {
          success: false,
          error: {
            code: 'UNKNOWN_CIRRA_SETTING',
            message: `Unknown Cirra setting "${rawKey}".`
          }
        };
      }

      continue;
    }

    if (allowedPersistences && !allowedPersistences.has(definition.persistence)) {
      return {
        success: false,
        error: {
          code: 'INVALID_CIRRA_SETTING_SCOPE',
          message: `Setting "${definition.key}" is not allowed in this persistence scope.`
        }
      };
    }

    const parsed = parseStrictSettingValue({
      definition,
      value: rawValue
    });

    if (!parsed.success) {
      return parsed;
    }

    normalizedPatch[definition.key] = parsed.data;
  }

  return {
    success: true,
    data: normalizedPatch
  };
};

export const getCirraPrefKeyFromSettingPath = (settingPath) => {
  const normalizedPath = String(settingPath || '').trim();

  if (!isCirraSyncedSettingKey(normalizedPath)) {
    return null;
  }

  return `${CIRRA_PREF_KEY_PREFIX}${normalizedPath}`;
};

export const getCirraSettingPathFromPrefKey = (prefKey) => {
  const normalizedPrefKey = String(prefKey || '').trim();

  if (!normalizedPrefKey.startsWith(CIRRA_PREF_KEY_PREFIX)) {
    return null;
  }

  const settingPath = normalizedPrefKey.slice(CIRRA_PREF_KEY_PREFIX.length);

  if (!isCirraSyncedSettingKey(settingPath)) {
    return null;
  }

  return settingPath;
};

export const normalizeCirraPrefEntry = ({ prefKey, value, operation = null } = {}) => {
  const settingPath = getCirraSettingPathFromPrefKey(prefKey);

  if (!settingPath) {
    return {
      success: false,
      error: {
        code: 'UNKNOWN_CIRRA_SETTING',
        message: `Unknown Cirra synced preference "${prefKey}".`
      }
    };
  }

  if (operation === 'delete') {
    return {
      success: true,
      data: {
        prefKey: getCirraPrefKeyFromSettingPath(settingPath),
        settingPath,
        operation: 'delete'
      }
    };
  }

  const parsedPatch = validateCirraSettingsPatch(
    {
      [settingPath]: value
    },
    {
      persistences: ['synced']
    }
  );

  if (!parsedPatch.success) {
    return parsedPatch;
  }

  return {
    success: true,
    data: {
      prefKey: getCirraPrefKeyFromSettingPath(settingPath),
      settingPath,
      value: parsedPatch.data[settingPath]
    }
  };
};

export const hasCirraSettingsPatchValues = (settingsPatch = {}) => {
  if (!isObject(settingsPatch)) {
    return false;
  }

  return Object.keys(settingsPatch).some((settingKey) => {
    return isCirraSettingKey(settingKey) && settingsPatch[settingKey] !== undefined;
  });
};

export const pickCirraSettingsByPersistence = (settings = {}, persistence) => {
  if (!isObject(settings)) {
    return {};
  }

  const nextSettings = {};

  for (const definition of cirraSettingsDefinitions) {
    if (definition.persistence !== persistence) {
      continue;
    }

    if (!Object.prototype.hasOwnProperty.call(settings, definition.key)) {
      continue;
    }

    nextSettings[definition.key] = settings[definition.key];
  }

  return nextSettings;
};

export const splitCirraSettingsPatchByPersistence = (settingsPatch = {}) => {
  const normalizedPatchRes = validateCirraSettingsPatch(settingsPatch, {
    rejectUnknownKeys: false
  });
  const sourcePatch = normalizedPatchRes.success ? normalizedPatchRes.data : {};
  const syncedPatch = {};
  const localPatch = {};

  for (const [settingKey, settingValue] of Object.entries(sourcePatch)) {
    const definition = getSettingDefinition(settingKey);

    if (!definition) {
      continue;
    }

    if (definition.persistence === 'synced') {
      syncedPatch[settingKey] = settingValue;
      continue;
    }

    localPatch[settingKey] = settingValue;
  }

  return {
    syncedPatch,
    localPatch
  };
};

export const getCirraPrefEntriesFromSettingsPatch = (settingsPatch = {}) => {
  if (!isObject(settingsPatch)) {
    return [];
  }

  const prefEntries = [];

  for (const settingPath of CIRRA_SYNCED_SETTING_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(settingsPatch, settingPath)) {
      continue;
    }

    const rawSettingValue = settingsPatch[settingPath];

    const normalizedPatchRes = validateCirraSettingsPatch(
      {
        [settingPath]: rawSettingValue
      },
      {
        persistences: ['synced']
      }
    );

    if (!normalizedPatchRes.success) {
      continue;
    }

    const settingValue = normalizedPatchRes.data[settingPath];

    prefEntries.push({
      settingPath,
      key: getCirraPrefKeyFromSettingPath(settingPath),
      value: settingValue
    });
  }

  return prefEntries;
};

export const getCirraSettingsPatchFromPrefMap = (prefMap = {}) => {
  if (!isObject(prefMap)) {
    return {};
  }

  const patch = {};

  for (const settingKey of CIRRA_SYNCED_SETTING_KEYS) {
    const prefKey = getCirraPrefKeyFromSettingPath(settingKey);
    const prefValue = prefMap?.cirra?.settings?.[settingKey];

    if (prefValue === undefined || !prefKey) {
      continue;
    }

    patch[settingKey] = prefValue;
  }

  return patch;
};

export const getCirraSyncedPrefMap = (prefMap = {}) => {
  if (!isObject(prefMap) || !isObject(prefMap?.cirra) || !isObject(prefMap?.cirra?.settings)) {
    return {};
  }

  const syncedPrefMap = {};

  for (const settingKey of CIRRA_SYNCED_SETTING_KEYS) {
    const prefKey = getCirraPrefKeyFromSettingPath(settingKey);
    const prefValue = prefMap.cirra.settings[settingKey];

    if (prefValue === undefined || !prefKey) {
      continue;
    }

    if (!syncedPrefMap.cirra) {
      syncedPrefMap.cirra = {};
    }

    if (!syncedPrefMap.cirra.settings) {
      syncedPrefMap.cirra.settings = {};
    }

    syncedPrefMap.cirra.settings[settingKey] = prefValue;
  }

  return syncedPrefMap;
};

export const normalizeCirraSettings = (settings, defaultsOverride) => {
  const defaultsSource = isObject(defaultsOverride) ? getCirraSettingsPatchFromSource(defaultsOverride) : {};
  const defaults = {
    ...getDefaultCirraSettings(),
    ...defaultsSource
  };
  const source = getCirraSettingsPatchFromSource(settings);
  const normalized = {};

  for (const definition of cirraSettingsDefinitions) {
    normalized[definition.key] = normalizeSettingValueLoose({
      definition,
      value: source[definition.key],
      fallback: defaults[definition.key]
    });
  }

  normalized.useOpaqueWindowBackground = true;

  return normalized;
};

export const mergeCirraSettings = ({ current, patch, defaults }) => {
  const currentNormalized = normalizeCirraSettings(current, defaults);
  const sourcePatch = getCirraSettingsPatchFromSource(patch);

  return normalizeCirraSettings(
    {
      ...currentNormalized,
      ...sourcePatch
    },
    defaults
  );
};

export const resolveCirraAutoTheme = ({ date } = {}) => {
  const currentDate = getSafeDate(date);
  const hour = currentDate.getHours();

  if (hour >= CIRRA_AUTO_THEME_NIGHT_START_HOUR || hour < CIRRA_AUTO_THEME_DAY_START_HOUR) {
    return 'dark';
  }

  return 'light';
};

export const getNextCirraAutoThemeChangeDelayMs = ({ date } = {}) => {
  const currentDate = getSafeDate(date);
  const nextBoundary = new Date(currentDate);
  const hour = currentDate.getHours();
  const isNight = hour >= CIRRA_AUTO_THEME_NIGHT_START_HOUR || hour < CIRRA_AUTO_THEME_DAY_START_HOUR;

  if (isNight) {
    if (hour >= CIRRA_AUTO_THEME_NIGHT_START_HOUR) {
      nextBoundary.setDate(nextBoundary.getDate() + 1);
    }

    nextBoundary.setHours(CIRRA_AUTO_THEME_DAY_START_HOUR, 0, 0, 0);
  } else {
    nextBoundary.setHours(CIRRA_AUTO_THEME_NIGHT_START_HOUR, 0, 0, 0);
  }

  return Math.max(1000, nextBoundary.getTime() - currentDate.getTime());
};

export const getResolvedCirraTheme = (themeSetting, options = {}) => {
  const normalizedTheme = THEME_VALUES.includes(normalizeEnumValue(themeSetting)) ? normalizeEnumValue(themeSetting) : 'system';

  if (normalizedTheme === 'auto') {
    return resolveCirraAutoTheme({
      date: options?.date
    });
  }

  if (normalizedTheme === 'system') {
    if (typeof options?.isSystemDark === 'boolean') {
      return options.isSystemDark ? 'dark' : 'light';
    }

    const matchMediaFn = typeof options?.matchMedia === 'function' ? options.matchMedia : typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : null;

    if (typeof matchMediaFn === 'function') {
      try {
        return matchMediaFn('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      } catch (err) {
        return 'dark';
      }
    }

    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    return 'dark';
  }

  return normalizedTheme;
};

export const getCirraUiScale = (sansFontSizePx) => {
  const parsedSansSize = Number(sansFontSizePx);
  const baseSansSize = Number(getDefaultCirraSettings()?.sansFontSizePx || DEFAULT_CIRRA_SANS_FONT_SIZE_PX);

  if (!Number.isFinite(parsedSansSize) || parsedSansSize <= 0 || !Number.isFinite(baseSansSize) || baseSansSize <= 0) {
    return 1;
  }

  return parsedSansSize / baseSansSize;
};

export { CIRRA_PREF_KEY_PREFIX, DEFAULT_CIRRA_SANS_FONT_SIZE_PX, DEFAULT_GENERAL_OPEN_DESTINATION_OPTIONS, GENERAL_OPEN_DESTINATION_VALUES, GENERAL_THREAD_DETAIL_OPTIONS, GENERAL_THREAD_DETAIL_VALUES, PERSONALIZATION_PERSONALITY_VALUES, PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_OPTIONS, PERSONALIZATION_PREFERRED_RESPONSE_LANGUAGE_VALUES, THEME_VALUES };
